<template>
  <div class="row">
    <div class="col-12 rounded-1 p-2" style="background-color: #E7E7E7 !important; display: inline-block;">
      <div>
        <div>
          <NodeActions
            :allSelectedNodes="allSelectedNodes"
            :collectionSlug="collection.slug"
            :currentlySelectedNode="currentlySelectedNode"
            :selectedMedia="selectedMedia"
            :selectedNodeIsFolder="selectedNodeIsFolder"
            :selectedNodeIsMedia="selectedNodeIsMedia"
            :selectedNodeIsRoot="selectedNodeIsRoot"
            :tree="treeData"
            @reset-selected-nodes="resetSelectedNodes"
          />
          <span v-if="!currentlySelectedNode" class="fs-5 fw-semibold">Select an item from this collection to see the available options.</span>
          <span v-if="showShareCollectionBtn" id="share_collection" data-test="share_collection" :title="$t('OrganizedTreeEditor.share-collection')" @click.prevent="showShareCollectionModal = true">
            <i class="fa-solid fa-share-nodes fa-2x mx-3 pointer"></i>
          </span>
          <span v-if="showMassDeleteBtn" id="mass_delete_items" data-test="mass_delete_items" title="Delete elements" @click="deleteSelectedNodes()">
            <i class="fa-solid fa-trash-can-xmark fa-2x mx-3 pointer"></i>
          </span>
        </div>
      </div>
    </div>
    <div class="col-12 my-3" style="max-height: 300px; overflow: auto;">
      <v-jstree ref="treeEditor" :data="treeData" show-checkbox multiple :collapse=false draggable allow-batch no-dots @item-click="nodeSelected">
        <template slot-scope="_">
          <div style="display: inherit;">
            <i v-if="!_.model.loading" role="presentation" :class="_.vm.themeIconClasses"></i>
            <span :data-slug="_.model.slug">{{ _.model.text }}</span>
          </div>
        </template>
      </v-jstree>
    </div>
    <div v-if="selectedNodeIsFolder && allSelectedNodes.length <= 1" class="col-12">
      <template v-if="!selectedNodeIsRoot">
        <strong>{{ $t('network.networkForm.title') }}</strong>
        <input id="selected_node" v-model="currentlySelectedNode.model.text" data-test="selected_node" type="text" :placeholder="this.$t('OrganizedTreeEditor.rename-node')" class="form-control w-100 mb-3" />
        <strong>{{ $t('network.networkForm.aboutThisSection') }}</strong>
        <Editor
          v-model="currentlySelectedNode.model.description"
          :api-key="tinyMceApiKey"
          :init="{
            height: 300,
            menubar: false,
            automatic_uploads: true,
            browser_spellcheck: true,
            paste_as_text: true,
            relative_urls : false,
            remove_script_host : false,
            plugins: [
              'advlist autolink lists link charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code help wordcount',
            ],
            toolbar:
              'undo redo | formatselect | bold italic backcolor | \
              alignleft aligncenter alignright alignjustify | \
              bullist numlist outdent indent | removeformat | help',
          }"
        />
      </template>
      <div class="mt-4">
        <h5>{{ $t('OrganizedTreeEditor.add-existing-media-to-folder') }}</h5>
        <v-select
          id="add_media"
          data-test="add_media"
          :options="existingMediaNotInCollection"
          label="title"
          :placeholder="$t('network.networkForm.selectExistingMedia')"
          @input="addMediaToCollectionOnSelectedFolder"
          @search="searchMediaInNetworksAndFilterExistingMedia"
          >
          <template v-slot:option="option">
            {{ option.title }}
          </template>
          <template v-slot:no-options>
            <span v-if="busy" class="text-center">
              <i class="fas fa-2x fa-spinner mt-2"></i>
            </span>
            <span v-else>
              <span v-if="selectedMedia.length > 0">{{ $t('sorry-there-are-no-matching-options') }}</span>
              <span v-else>Select media to add to this collection</span>
            </span>
          </template>
        </v-select>
      </div>
      <div v-if="this.$route.name != 'create-network-collection-page'" class="mt-4">
        <h5>{{ $t('OrganizedTreeEditor.add-new-media-to-folder') }}</h5>
        <CreateMediaWithoutCollection
          :key="createMediaComponentKey"
          :networks="collection.networks"
          :collectionId="collection.id"
          @media-added-to-collection="addMediaToCollectionAfterNewUpload"
        ></CreateMediaWithoutCollection>
      </div>
    </div>
    <div v-if="selectedNodeIsMedia && allSelectedNodes.length <= 1" class="col-12 px-0">
      <div class="sticky-top-custom">
          <NetworkMediaForm
            ref="networkMediaForm"
            :key="mediaFormReloadKey"
            :autosave="true"
            :managedNetworks="managedNetworks"
            :media="media"
            :mediaForm="mediaForm"
            :inCollection="true"
          />
      </div>
    </div>

    <b-modal v-model="showShareCollectionModal" hide-header hide-footer centered>
      <ShareCollection :networks="managedNetworks" :tree-data="treeData" />
    </b-modal>
  </div>
</template>
<script>
import { OrganizedTreeService } from '@/services/OrganizedTreeService.js';
import CreateMediaWithoutCollection from '@/views/networks/media/CreateMediaWithoutCollection.vue';
import NetworkMediaForm from '@/components/forms/media/NetworkMediaForm.vue';
import NodeActions from '@/components/forms/collection/NodeActions.vue';
import ShareCollection from './ShareCollectionToNetwork.vue';
import Editor from '@tinymce/tinymce-vue';
import VJstree from 'vue-jstree';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { orderBy, debounce} from 'lodash';

export default {
  components: {
    CreateMediaWithoutCollection,
    NetworkMediaForm,
    NodeActions,
    ShareCollection,
    Editor,
    VJstree,
    vSelect,
  },
  props: {
    collection: {
      type: Object,
      default: null,
    },
    managedNetworks: {
      type: Array,
      default: null,
    },
  },
  data() {
    let treeData;
    let treeService = new OrganizedTreeService();
    if (!this.collection.organized_data) {
      treeData = treeService.getNewTree(this.collection.collection, this.collection.name);
    } else {
      treeData = treeService.getExistingTree(JSON.parse(this.collection.organized_data), this.collection.name);
    }
    return {
      existingMediaNotInCollection: [],
      showShareCollectionModal: false,
      createMediaComponentKey: 1,
      treeData,
      mediaFormReloadKey: 1,
      busy: false,
      selectedMedia: null,
      treeService,
      mediaForm: null,
      currentlySelectedNode: null,
      allSelectedNodes: [],
      tinyMceApiKey: process.env.VUE_APP_TINYMCE_API_KEY,
    };
  },
  computed: {
    media() {
      return this.collection.collection;
    },
    selectedNodeIsFolder() {
      return this.currentlySelectedNode && (this.currentlySelectedNode.model.type == 'folder' || this.currentlySelectedNode.model.type == 'root');
    },
    selectedNodeIsMedia() {
      return this.currentlySelectedNode && (this.currentlySelectedNode.model.type == 'file' || this.currentlySelectedNode.model.type == 'video');
    },
    selectedNodeIsRoot() {
      return this.currentlySelectedNode && this.currentlySelectedNode.model.type == 'root';
    },
    showShareCollectionBtn() {
      return this.allSelectedNodes.length > 1 || (this.allSelectedNodes.length === 1 && this.selectedNodeIsFolder);
    },
    showMassDeleteBtn() {
      return this.allSelectedNodes.length > 1;
    },
  },
  /*watch: {
    'currentlySelectedNode.model.text': {
      handler: debounce(function () {
        if (this.selectedNodeIsFolder && this.currentlySelectedNode.model.type != 'root') {
          this.$bus.emit('collection-update-event');
        }
      }, 3000),
      deep: true,
    },
    'currentlySelectedNode.model.description': {
      handler: debounce(function () {
        if (this.selectedNodeIsFolder && this.currentlySelectedNode.model.type != 'root') {
          this.$bus.emit('collection-update-event');
        }
      }, 3000),
      deep: true,
    },
  },*/
  mounted() {
    this.$bus.on('network-media-form-submit', form => this.onMediaEditSubmitEvent(form));
    this.$bus.on('collection-share-complete', () => {
      this.showShareCollectionModal = false;
    });
  },
  beforeDestroy() {
    this.$bus.off('network-media-form-submit');
  },
  methods: {
    handleMultipleNodeSelection(node) {
      this.allSelectedNodes.splice(0);
      this.treeService.getAllCheckedNodesInTree(this.$refs.treeEditor.$children[0], this.allSelectedNodes);

      if (!node.data.selected) {
        this.currentlySelectedNode = this.allSelectedNodes.slice(-1).pop() || null;
      }
    },
    nodeSelected(node) {
      this.handleMultipleNodeSelection(node);
      if (!node.data.selected) {
        return;
      }
      this.mediaFormReloadKey++;
      this.currentlySelectedNode = node;
      this.selectedMedia = Object.assign({}, this.selectedMedia, null);
      if (node.model.slug && node.model.type !== 'folder') {
        this.selectedMedia = this.media.find(m => node.model.slug == m.slug);
        this.setupMediaForm();
      }
    },
    searchMediaInTree(node, slug) {
      if (node.model.slug == slug) return node;
    },
    setupMediaForm() {
      this.mediaForm = Object.assign({}, this.mediaForm, {
        id: this.selectedMedia.id,
        title: this.selectedMedia.title,
        slug: this.selectedMedia.slug,
        permission: this.selectedMedia.permission,
        presenters: this.selectedMedia.presenters ? this.selectedMedia.presenters : this.selectedMedia.presenter_text,
        description: this.selectedMedia.description,
        type: this.selectedMedia.type,
        language: this.selectedMedia.language,
        tags: this.selectedMedia.tags,
        publish_date: this.selectedMedia.publish_date,
        networks: this.selectedMedia.networks,
        attachments: this.selectedMedia.attachments,
      });
    },
    onMediaEditSubmitEvent(form) {
      this.currentlySelectedNode.model.text = form.title;
      this.busy = true;
      this.$http
        .put('networks/media/' + this.selectedMedia.slug, form)
        .then(() => {
          this.$bus.emit('collection-update-event');
        })
        .finally(() => {
          this.busy = false;
        });
    },
    exportTreeDataToJson() {
      let tree = JSON.parse(JSON.stringify(this.treeData[0]));
      return this.treeService.formatTreeDataToOrganizedCollection(tree).children;
    },
    appendNewMediaToExisting(addedMedia) {
      addedMedia = Array.isArray(addedMedia) ? addedMedia : [addedMedia];
      let currentMedia = JSON.parse(JSON.stringify(this.media));
      addedMedia.forEach(m => currentMedia.push(m));
      //this.$bus.emit('collection-update-event');
      return currentMedia;
    },
    addMediaToCollectionOnSelectedFolder(media) {
      if (!media) return;
      media = this.appendNewMediaToExisting(media);
      this.$emit('update-collection-media-list', media);
      this.mediaAddedOrRemovedOnCollection(media);
      this.$bus.emit('collection-update-event');
    },
    addMediaToCollectionAfterNewUpload(addedMedia) {
      this.createMediaComponentKey++;
      this.addMediaToCollectionOnSelectedFolder(this.appendNewMediaToExisting(addedMedia));
      //this.$bus.emit('collection-update-event');
    },
    mediaAddedOrRemovedOnCollection(media) {
      // check all media
      let treeMediaNodes = [];
      this.treeService.getAllMediaInTree(this.treeData, treeMediaNodes);

      // if new media found append to root of tree
      let mediaToAppend = media.filter(singleMedia => !treeMediaNodes.find(node => singleMedia.slug == node.slug));
      let node = this.currentlySelectedNode ? this.currentlySelectedNode.model : this.treeData[0]; // add to currentlySelectedNode or root
      mediaToAppend.forEach(m => node.addChild(this.treeService.getMediaTreeObject(m)));

      // if any media found in tree that is not in list remove
      treeMediaNodes
        .filter(node => {
          return !media.find(singleMedia => node.slug == singleMedia.slug);
        })
        .forEach(n => {
          this.treeService.deleteNode(n.slug, this.treeData, null);
        });
      //this.$bus.emit('collection-update-event');
    },
    searchMediaInNetworksAndFilterExistingMedia: debounce(function (searchQuery) {
      this.busy = true;
      this.existingMediaNotInCollection.splice(0);
      if (searchQuery.length === 0) {
        this.busy = false;
        return;
      }
      let params = {
        query: searchQuery,
        media_type: 'media',
        for_selector: true,
        network_id: this.managedNetworks.map(n => n.id).toString(),
      };
      this.$http.get('/networks/feed', { params }).then(resp => {
        const sortedResults = orderBy(resp.data.results, 'title');
        sortedResults.forEach(m1 => {
          let mediaExistsInCollection = this.media.find(m2 => m1.slug === m2.slug);
          if (!mediaExistsInCollection) {
            this.existingMediaNotInCollection.push(m1);
          }
        });
        this.busy = false;
      });
    }, 600), // 600ms debounce delay
    resetSelectedNodes() {
      this.currentlySelectedNode = null;
      this.selectedMedia = null;
      this.allSelectedNodes.splice(0);
    },
    deleteSelectedNodes () {
      this.$swal.fire({
        //icon: 'warning',
        title: 'Are you sure you want to delete these items?',
        showCancelButton: true,
        showCloseButton: true,
        confirmButtonText: 'Delete',
        customClass: {
          confirmButton: 'btn btn-danger mx-2',
          cancelButton: 'btn btn-primary mx-2',
        },
        buttonsStyling: false
      }).then((result) => {
        if (result.isConfirmed) {
          this.allSelectedNodes.forEach(node => {
            this.treeService.deleteNode(node.model.slug, this.treeData, null);
            //this.$bus.emit('collection-update-event');
          });
        }
      });
    },
  },
};
</script>
<style>
.sticky-top-custom {
  top: 60px;
  position: -webkit-sticky;
  position: sticky;
}
</style>
